import { useCallback } from "react"

import moment from "moment"
import { useTranslation } from "react-i18next"
import { Icon } from "semantic-ui-react"
import type { OpenAPI } from "simplydo/interfaces"
import { styled } from "styled-components"

import FormattedComment from "components/lib/FormattedComment"
import { eventTypes } from "components/lib/ProjectBoard/BoardEvents"
import { EmptyBox } from "components/lib/UI"

const EventItem = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  .icon-container {
    .i {
      font-size: 0.9em;
      line-height: 1.1em;
    }
    margin-right: 10px;
  }
  .info-container {
    > span {
      display: block;
    }
    .meta {
      color: gray;
    }
  }
  margin-bottom: 10px;
`

type ManagementActivityProps = {
  idea: OpenAPI.GET<"/ideas/{id}">["response"]
  userIsAssigned?: boolean
  activityQuery: any
}

const ActivityManagement = ({ idea, activityQuery }: ManagementActivityProps) => {
  const { t } = useTranslation()

  const getLaneName = useCallback(
    (event, laneKey = "previousLane") => {
      // If previousLane is empty string/null, we know it was moved from default lane
      if (!event[laneKey]) {
        return idea?.ownerChallenge?.projectBoard?.defaultLane?.name ?? "Default"
      }
      if (event[`${laneKey}Object`]) {
        return event[`${laneKey}Object`].name
      }
      return "an unknown lane"
      // Else if we know the previous lane object, we can return that
    },
    [idea?.ownerChallenge],
  )

  if (activityQuery.data?.events?.length === 0)
    return <EmptyBox>This {t("generic.idea")} currently has no project board activity.</EmptyBox>
  return (
    <>
      {activityQuery.data?.events?.map((e) =>
        eventTypes[e.action] ? (
          <EventItem key={e._id}>
            <div className="icon-container">
              <Icon name={eventTypes[e.action].icon} />
            </div>
            <div className="info-container">
              <span>
                <b>{e.userObject ? `${e.userObject.profile.fullName}` : "Unknown user"}</b>
                {e.action === "addedToBoard" && ` added the ${t("generic.idea")} to the board`}
                {e.action === "removedFromBoard" && ` removed the ${t("generic.idea")} from the board`}
                {e.action === "assignedUser" && (
                  <>
                    {e.userObject ? " assigned " : " auto-assigned "}
                    <b>{e.targetUserObject ? `${e.targetUserObject.profile.fullName}` : "unknown user"}</b>
                  </>
                )}
                {e.action === "assignedExternalUser" && (
                  <>
                    {" invited "}
                    <b>{e.email}</b>
                  </>
                )}
                {e.action === "unassignedUser" && (
                  <>
                    {" un-assigned "}
                    <b>{e.targetUserObject ? `${e.targetUserObject.profile.fullName}` : "unknown user"}</b>
                  </>
                )}
                {e.action === "changedLane" && (
                  <>
                    {" moved from "}
                    <b>{getLaneName(e)}</b>
                    {" to "}
                    <b>{getLaneName(e, "targetLane")}</b>
                  </>
                )}
                {e.action === "changedDeadline" && (
                  <>
                    {" changed the deadline to "}
                    <b>{e.deadline ? moment(e.deadline).format("DD/MM/YYYY") : "No deadline"}</b>
                  </>
                )}
                {e.action === "changedReviewDate" && (
                  <>
                    {" changed the review date to "}
                    <b>{e.reviewDate ? moment(e.reviewDate).format("DD/MM/YYYY") : "No review date"}</b>
                  </>
                )}
                {e.action === "createComment" && (
                  <>
                    {" added a project note. Note: "}
                    <b
                      style={{
                        maxWidth: "590px",
                        display: "block",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      <FormattedComment>{e?.comment}</FormattedComment>
                    </b>
                  </>
                )}
                {e.action === "ideaAssessorAssigned" && <>{" was assigned as an assessor."}</>}
                {e.action === "assessmentSubmitted" && <>{" submitted an assessment."}</>}
                {e.action === "assessmentHasConflict" && <>{" declared a conflict of interest."}</>}
              </span>
              <span className="meta">{moment(e.createdAt).fromNow()}</span>
            </div>
          </EventItem>
        ) : null,
      )}
    </>
  )
}

export default ActivityManagement
